import React, { useState, useMemo } from "react";
import orderApi, { ProductData, GeneratedProduct } from "../services/orderApi";
import config from "../config";
import { formatCurrency } from "../utils/format";
import { notification } from "../services/notification";

const ITEMS_PER_PAGE = 20;

interface AdminProductsProps {
  products: GeneratedProduct[];
  onProductsUpdate: () => void;
}

function AdminProducts({ products, onProductsUpdate }: AdminProductsProps) {
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProduct, setEditProduct] = useState<GeneratedProduct | null>(null);
  const [saving, setSaving] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Form State - Create
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [sku, setSku] = useState("");              // NEW: SKU
  const [stock, setStock] = useState("");          // NEW: Stock quantity
  const [stockVisible, setStockVisible] = useState(false); // NEW: Stock visibility toggle
  const [allowCOD, setAllowCOD] = useState(false); // NEW: Allow COD

  // Form State - Edit
  const [editName, setEditName] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editShippingCost, setEditShippingCost] = useState("");
  const [editStock, setEditStock] = useState("");
  const [editSku, setEditSku] = useState("");
  const [editStockVisible, setEditStockVisible] = useState(false);
  const [editAllowCOD, setEditAllowCOD] = useState(false);

  const [generatedUrl, setGeneratedUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Helper function to generate random SKU
  const generateNewSKU = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let newSku = 'SKU-';
    for (let i = 0; i < 8; i++) {
      newSku += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return newSku;
  };

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    const query = searchQuery.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(query) ||
      p.productId?.toLowerCase().includes(query) ||
      p.notes?.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPageNum - 1) * ITEMS_PER_PAGE,
    currentPageNum * ITEMS_PER_PAGE
  );

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    // Validate required fields
    if (!stock || parseInt(stock) <= 0) {
      notification.error("Stock quantity must be greater than 0");
      setSaving(false);
      return;
    }

    const productPrice = parseFloat(price) || 0;
    const prodShipping = parseFloat(shippingCost) || 0;
    const prodStock = parseInt(stock) || 0;

    const productData: ProductData = {
      name,
      price: productPrice,
      imageUrl,
      notes,
      shippingCost: prodShipping,
      cartUrl: "",
      sku: sku || "", // Use generated or custom SKU
      stock: prodStock,
      stockVisible,
      allowCOD
    };

    try {
      const res = await orderApi.createProduct(productData);
      if (res.success && res.productId) {
        // Generate URL with productId
        let params = new URLSearchParams();
        params.append("productId", res.productId);
        


        // Do not attach parameters - rely on the API.
        const cartUrl = `${config.host_url}/cart?${params.toString()}`;
        setGeneratedUrl(cartUrl);

        // Save cart URL to sheet
        await orderApi.updateProductCartUrl(res.productId, cartUrl);

        notification.success("Product created successfully!");
        onProductsUpdate();
        setShowModal(false);
        // Reset form
        setName("");
        setPrice("");
        setImageUrl("");
        setNotes("");
        setShippingCost("");
        setSku("");
        setStock("");
        setStockVisible(false);
        setAllowCOD(false);
        setEditAllowCOD(false);
      } else {
        notification.error(res.error || "Failed to create product");
      }
    } catch (err) {
      console.error("Error creating product:", err);
      notification.error("Error creating product");
    }

    setSaving(false);
  };

  const copyToClipboard = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: "700", marginBottom: "4px" }}>Products</h2>
          <p style={{ color: "#6b7280", margin: 0 }}>{filteredProducts.length} generated products</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
           <button onClick={onProductsUpdate} style={{ background: "white", color: "#6366f1", padding: "10px 16px", borderRadius: "10px", border: "1px solid #e5e7eb", cursor: "pointer", fontWeight: "600" }}>
             <i className="fas fa-sync-alt"></i> Refresh
           </button>
           <button
             onClick={() => {
               // Generate new SKU when opening modal
               const newSku = generateNewSKU();
               setSku(newSku);
               setName(""); setPrice(""); setImageUrl(""); setNotes(""); setShippingCost(""); setGeneratedUrl("");
               setStock("");
               setStockVisible(false);
               setShowModal(true);
             }}
             style={{ background: "linear-gradient(135deg, #10b981, #34d399)", color: "white", padding: "10px 16px", borderRadius: "10px", border: "none", cursor: "pointer", fontWeight: "600" }}
           >
             <i className="fas fa-plus"></i> Generate Product
           </button>
         </div>
      </div>

      {/* Search Filter */}
      <div style={{ background: "white", padding: "16px", borderRadius: "12px", marginBottom: "16px" }}>
          <input 
            type="text" 

            placeholder="Search..." 
            value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPageNum(1); }}
          style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} 
        />
      </div>

      <div style={{ background: "white", borderRadius: "12px", overflow: "hidden" }}>
        {filteredProducts.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center", color: "#6b7280" }}>No products found</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Image</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Product ID</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Name</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>SKU</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Stock</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Price</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Shipping</th>
                  <th style={{ padding: "12px", textAlign: "left", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Notes</th>
                  <th style={{ padding: "12px", textAlign: "center", fontSize: "0.7rem", fontWeight: "600", color: "#6b7280" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((prod) => (
                  <tr key={prod.productId} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {prod.imageUrl ? (
                        <img src={prod.imageUrl} alt={prod.name} style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: "40px", height: "40px", borderRadius: "6px", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <i className="fas fa-image" style={{ color: "#9ca3af", fontSize: "16px" }}></i>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px", fontSize: "0.75rem", color: "#6b7280" }}>{prod.productId}</td>
                    <td style={{ padding: "12px", fontWeight: "500", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={prod.name}>{prod.name}</td>
                    <td style={{ padding: "12px", textAlign: "center", fontSize: "0.75rem", color: "#6366f1", fontFamily: "monospace", fontWeight: "600" }}>{prod.sku || "-"}</td>
                    <td style={{ padding: "12px", textAlign: "center", fontWeight: "600", color: prod.stock === 0 ? "#ef4444" : "#10b981" }}>
                      {prod.stock !== undefined ? prod.stock : "-"}
                    </td>
                     <td style={{ padding: "12px", textAlign: "center", color: "#f97316", fontWeight: "600" }}>{formatCurrency(prod.price)}</td>
                     <td style={{ padding: "12px", textAlign: "center" }}>{formatCurrency(prod.shippingCost || 0)}</td>
                    <td style={{ padding: "12px", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={prod.notes}>{prod.notes || "-"}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          aria-label="Copy URL"
                          onClick={() => { 
                            const url = prod.cartUrl || `${config.host_url}/cart?productId=${prod.productId}`;
                            navigator.clipboard.writeText(url);
                            notification.success("URL copied to clipboard!");
                          }}
                          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#6366f1" }}
                          title="Copy URL"
                        >
                          <i className="far fa-copy"></i>
                        </button>
                        <a 
                          href={prod.cartUrl || `${config.host_url}/cart?productId=${prod.productId}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          aria-label="Open in New Tab"
                          title="Open in New Tab"
                          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#10b981", textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                        >
                          <i className="fas fa-external-link-alt"></i>
                        </a>
                        <button
                          aria-label="Edit Product"
                          onClick={() => {
                            setEditProduct(prod);
                            setEditName(prod.name || "");
                            setEditPrice(prod.price?.toString() || "");
                            setEditImageUrl(prod.imageUrl || "");
                            setEditNotes(prod.notes || "");
                            setEditShippingCost(prod.shippingCost?.toString() || "");
                            setEditStock(prod.stock?.toString() || "0");
                            setEditSku(prod.sku || "");
                            setEditStockVisible(prod.stockVisible || false);
                            setEditAllowCOD(prod.allowCOD || false);
                            setShowEditModal(true);
                          }}
                          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#f59e0b" }}
                          title="Edit Product"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={async () => {
                            const confirmed = await notification.confirm(
                              "Delete Product?",
                              `Are you sure you want to delete <strong>${prod.name}</strong>? This action cannot be undone.`,
                              "Yes, Delete",
                              "Cancel"
                            );
                            if (confirmed) {
                              try {
                                const result = await orderApi.deleteProduct(prod.productId);
                                if (result.success) {
                                  notification.success("Product deleted successfully!");
                                  onProductsUpdate();
                                } else {
                                  notification.error(result.error || "Failed to delete product");
                                }
                              } catch (err) {
                                notification.error("Error deleting product");
                                console.error(err);
                              }
                            }
                          }}
                          aria-label="Delete Product"
                          title="Delete Product"
                          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", color: "#dc2626" }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ padding: "12px", borderTop: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>Page {currentPageNum} of {totalPages || 1}</span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button aria-label="Previous page" onClick={() => setCurrentPageNum(p => Math.max(1, p - 1))} disabled={currentPageNum <= 1} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: currentPageNum <= 1 ? "#f3f4f6" : "white", cursor: currentPageNum <= 1 ? "not-allowed" : "pointer" }}>Prev</button>
            <button aria-label="Next page" onClick={() => setCurrentPageNum(p => Math.min(totalPages, p + 1))} disabled={currentPageNum >= totalPages} style={{ padding: "6px 12px", borderRadius: "6px", border: "1px solid #e5e7eb", background: currentPageNum >= totalPages ? "#f3f4f6" : "white", cursor: currentPageNum >= totalPages ? "not-allowed" : "pointer" }}>Next</button>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1002, padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ fontWeight: "700", margin: 0 }}>Generate Product CTA</h3>
              <button onClick={() => setShowModal(false)} aria-label="Close modal" style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.2rem", color: "#9ca3af" }}>×</button>
            </div>

            <form onSubmit={handleGenerate}>
              <div style={{ display: "grid", gap: "12px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Product Name *</label>
                  <input required value={name} onChange={e => setName(e.target.value)} type="text" placeholder="e.g. Wireless Speaker" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Price ({config.branding.currency.symbol}) *</label>
                    <input required value={price} onChange={e => setPrice(e.target.value)} type="number" min="0" step="0.01" placeholder="900" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Stock Qty *</label>
                    <input required value={stock} onChange={e => setStock(e.target.value)} type="number" min="0" step="1" placeholder="10" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>SKU (auto-generated, can edit)</label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input value={sku} onChange={e => setSku(e.target.value)} type="text" placeholder="SKU-XXXXXXXX" style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                    <button 
                      type="button" 
                      onClick={() => setSku(generateNewSKU())}
                      title="Generate new SKU"
                      style={{ padding: "10px 12px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", fontSize: "1rem" }}
                    >
                      🔄
                    </button>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Shipping ({config.branding.currency.symbol})</label>
                    <input value={shippingCost} onChange={e => setShippingCost(e.target.value)} type="number" min="0" step="0.01" placeholder="0 or leave blank" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", justifyContent: "flex-end" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", cursor: "pointer" }}>
                      <input type="checkbox" id="allowCOD" checked={allowCOD} onChange={e => setAllowCOD(e.target.checked)} style={{ cursor: "pointer", accentColor: "#6366f1" }} />
                      <span>Allow Cash on Delivery (COD)</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", cursor: "pointer" }}>
                      <input type="checkbox" checked={stockVisible} onChange={e => setStockVisible(e.target.checked)} style={{ cursor: "pointer" }} />
                      <span>Show stock to customers</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Image URL</label>
                  <input value={imageUrl} onChange={e => setImageUrl(e.target.value)} type="url" placeholder="https://example.com/image.jpg" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Special instructions or product details..." style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }}></textarea>
                </div>
              </div>

              {generatedUrl && (
                <div style={{ background: "#f0fdf4", padding: "16px", borderRadius: "8px", marginBottom: "20px", border: "1px solid #bbf7d0" }}>
                  <p style={{ fontSize: "0.85rem", fontWeight: "600", color: "#166534", margin: "0 0 8px" }}>Generated Link:</p>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input type="text" readOnly value={generatedUrl} style={{ flex: 1, padding: "8px", fontSize: "0.85rem", border: "1px solid #bbf7d0", borderRadius: "4px", background: "white" }} />
                    <button type="button" onClick={copyToClipboard} style={{ padding: "8px 12px", background: "#10b981", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "600" }}>
                      {copied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer" }}>Close</button>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "none", background: saving ? "#9ca3af" : "linear-gradient(135deg, #3b82f6, #6366f1)", color: "white", fontWeight: "600", cursor: saving ? "not-allowed" : "pointer" }}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Generating...</> : "Generate Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editProduct && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1002, padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "16px", padding: "24px", width: "100%", maxWidth: "500px", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h3 style={{ fontWeight: "700", margin: 0 }}>Edit Product</h3>
              <button onClick={() => setShowEditModal(false)} aria-label="Close modal" style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.2rem", color: "#9ca3af" }}>×</button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setSaving(true);
              try {
                const result = await orderApi.updateProduct(editProduct.productId, {
                  name: editName,
                  price: Number(editPrice),
                  imageUrl: editImageUrl,
                  notes: editNotes,
                  shippingCost: Number(editShippingCost) || 0,
                  stock: Number(editStock) || 0,
                  sku: editSku,
                  stockVisible: editStockVisible,
                  allowCOD: editAllowCOD
                });
                if (result.success) {
                  notification.success("Product updated successfully!");
                  setShowEditModal(false);
                  onProductsUpdate();
                } else {
                  notification.error(result.error || "Failed to update product");
                }
              } catch (err) {
                console.error("Error updating product:", err);
                notification.error("Error updating product");
              }
              setSaving(false);
            }}>
              <div style={{ display: "grid", gap: "12px", marginBottom: "20px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Product Name *</label>
                  <input required value={editName} onChange={e => setEditName(e.target.value)} type="text" placeholder="e.g. Wireless Speaker" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Price ({config.branding.currency.symbol}) *</label>
                    <input required value={editPrice} onChange={e => setEditPrice(e.target.value)} type="number" min="0" step="0.01" placeholder="900" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Stock Qty *</label>
                    <input required value={editStock} onChange={e => setEditStock(e.target.value)} type="number" min="0" step="1" placeholder="10" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>SKU (auto-generated, can edit)</label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input value={editSku} onChange={e => setEditSku(e.target.value)} type="text" placeholder="SKU-XXXXXXXX" style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                    <button 
                      type="button" 
                      onClick={() => setEditSku(generateNewSKU())}
                      title="Generate new SKU"
                      style={{ padding: "10px 12px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer", fontSize: "1rem" }}
                    >
                      🔄
                    </button>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Shipping ({config.branding.currency.symbol})</label>
                    <input value={editShippingCost} onChange={e => setEditShippingCost(e.target.value)} type="number" min="0" step="0.01" placeholder="0 or leave blank" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", justifyContent: "flex-end" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", cursor: "pointer" }}>
                      <input type="checkbox" id="editAllowCOD" checked={editAllowCOD} onChange={e => setEditAllowCOD(e.target.checked)} style={{ cursor: "pointer", accentColor: "#6366f1" }} />
                      <span>Allow Cash on Delivery (COD)</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.85rem", cursor: "pointer" }}>
                      <input type="checkbox" checked={editStockVisible} onChange={e => setEditStockVisible(e.target.checked)} style={{ cursor: "pointer" }} />
                      <span>Show stock to customers</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Image URL</label>
                  <input value={editImageUrl} onChange={e => setEditImageUrl(e.target.value)} type="url" placeholder="https://example.com/image.jpg" style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }} />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.85rem", fontWeight: "600", marginBottom: "4px" }}>Notes</label>
                  <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={3} placeholder="Special instructions or product details..." style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb", boxSizing: "border-box" }}></textarea>
                </div>
              </div>

              <div style={{ display: "flex", gap: "12px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                <button type="button" onClick={() => setShowEditModal(false)} style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e5e7eb", background: "white", cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "none", background: saving ? "#9ca3af" : "linear-gradient(135deg, #f59e0b, #d97706)", color: "white", fontWeight: "600", cursor: saving ? "not-allowed" : "pointer" }}>
                  {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminProducts;